import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { KeyRound, ShieldCheck } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Brand } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";

export default function SetPassword() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/public/setup/${key}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(errMsg(e)));
  }, [key]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Those passwords do not match.");
    setBusy(true);
    setError("");
    try {
      await api.post(`/public/setup/${key}`, { password });
      await refreshUser();
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-3xl items-center px-5">
          <Brand />
        </div>
      </header>
      <main className="mx-auto max-w-md px-5 py-16">
        {!info && error ? (
          <div
            className="rounded-lg border border-red-200 bg-red-50 p-8 text-center"
            data-testid="setup-invalid"
          >
            <p className="font-display text-lg font-bold text-red-800">Link no longer valid</p>
            <p className="mt-2 text-sm text-red-700">{error}</p>
            <Button
              onClick={() => navigate("/login")}
              className="mt-6 h-11 rounded-full bg-blue-600 px-7 font-semibold hover:bg-blue-700"
            >
              Go to sign in
            </Button>
          </div>
        ) : info ? (
          <div className="ct-rise rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
            <h1 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-slate-900">
              Set your password
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Welcome{info.contact_name ? `, ${info.contact_name}` : ""}. Choose a password for{" "}
              <strong className="text-slate-900">{info.company_name}</strong>&rsquo;s ConfirmTerms
              portal.
            </p>
            <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Your username
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900" data-testid="setup-username">
                {info.email}
              </div>
            </div>
            <form onSubmit={submit} className="mt-6 space-y-5" data-testid="setup-form">
              {[
                ["New password", password, setPassword, "setup-password-input"],
                ["Confirm password", confirm, setConfirm, "setup-confirm-input"],
              ].map(([label, value, setter, tid]) => (
                <div key={tid}>
                  <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {label}
                  </Label>
                  <PasswordInput
                    required
                    minLength={8}
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    testId={tid}
                    className="mt-2"
                  />
                </div>
              ))}
              <p className="text-xs text-slate-500">At least 8 characters.</p>
              {error && (
                <div
                  className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                  data-testid="setup-error"
                >
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={busy}
                data-testid="setup-submit-button"
                className="h-11 w-full rounded-md bg-blue-600 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-[0.99]"
              >
                <KeyRound className="mr-2 h-4 w-4" />
                {busy ? "Saving…" : "Save password and sign in"}
              </Button>
            </form>
          </div>
        ) : (
          <div className="py-16 text-center text-sm text-slate-500">Loading…</div>
        )}
      </main>
    </div>
  );
}
